import React, { useEffect, useState } from "react";
import moment from "moment";
import {
    FeedRetryButton,
    FeedPinIcon,
    FeedPinIconButton,
    FeedSkeletonBlock,
    PinnedContent,
    PinnedCreatorRow,
    PinnedDescription,
    PinnedHeader,
    PinnedList,
    PinnedMeta,
    PinnedPostButton,
    PinnedPostItem,
    PinnedPostTitle,
    PinnedSidebar,
    PinnedState,
    PinnedThumbnail,
    PinnedThumbnailImage,
    PinnedTitle
} from "src/components/NoticeboardFeedUi";
import NoticePinIcon from "src/img/notice_pin.png";
import { unpinNoticePublication } from "../../api/feedApi";
import {
    getNoticeCommentCount,
    getNoticeAuthorIdentity,
    getNoticeInteractionUuid,
    getNoticeMediaItems,
    getNoticePublication,
    incrementNoticeCommentCount,
    isNoticePinnedForAudience,
    setNoticePublicationPinned
} from "../../feedUtils";
import NoticeboardFeedAvatar from "./NoticeboardFeedAvatar";
import NoticeboardPostDetailModal from "./NoticeboardPostDetailModal";
import NoticeboardReactionsModal from "./NoticeboardReactionsModal";

const PinnedSkeleton = () => (
    <PinnedPostButton type="button" disabled aria-hidden="true">
        <FeedSkeletonBlock $width="4rem" $height="4rem" $radius="8px" />
        <PinnedContent>
            <FeedSkeletonBlock $height="0.75rem" />
            <FeedSkeletonBlock $width="75%" $height="0.65rem" />
            <FeedSkeletonBlock $width="55%" $height="0.6rem" />
        </PinnedContent>
    </PinnedPostButton>
);

const PinnedMediaThumbnail = ({ thumbnailUrl }) => {
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        setImageFailed(false);
    }, [thumbnailUrl]);

    if (!thumbnailUrl || imageFailed) {
        return <span aria-hidden="true">📌</span>;
    }

    return (
        <PinnedThumbnailImage
            src={thumbnailUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImageFailed(true)}
        />
    );
};

const NoticeboardPinnedPosts = ({
    notices,
    loading,
    error,
    onRetry,
    token,
    audience,
    employeeName,
    clientName,
    canManageActiveNoticeboard,
    canUseCheckedIn,
    onNoticeUpdated
}) => {
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [showReactions, setShowReactions] = useState(false);
    const [unpinLoadingUuid, setUnpinLoadingUuid] = useState(null);
    const [unpinError, setUnpinError] = useState(false);
    const pinnedNotices = notices.filter(notice => isNoticePinnedForAudience(notice, audience));
    const selectedInteractionUuid = selectedNotice ?
        getNoticeInteractionUuid(selectedNotice, audience) :
        null;

    const handleSelectedNoticeUpdated = (updatedNotice) => {
        setSelectedNotice(updatedNotice);
        onNoticeUpdated(updatedNotice);
    };

    const handleCommentCreated = () => {
        if (!selectedNotice) {
            return;
        }

        handleSelectedNoticeUpdated(
            incrementNoticeCommentCount(selectedNotice, selectedInteractionUuid)
        );
    };
    const handleCloseDetail = () => {
        setShowReactions(false);
        setSelectedNotice(null);
    };
    const handleUnpin = async (notice) => {
        const publication = getNoticePublication(notice, audience);
        if (!publication || unpinLoadingUuid) {
            return;
        }

        setUnpinLoadingUuid(notice.uuid);
        setUnpinError(false);
        try {
            await unpinNoticePublication(token, publication.uuid);
            const updatedNotice = setNoticePublicationPinned(
                notice,
                publication.uuid,
                false
            );
            if (selectedNotice?.uuid === notice.uuid) {
                setSelectedNotice(updatedNotice);
            }
            onNoticeUpdated(updatedNotice);
        } catch {
            setUnpinError(true);
        } finally {
            setUnpinLoadingUuid(null);
        }
    };

    return (
        <>
            <PinnedSidebar aria-label="Pinned Noticeboard posts">
                <PinnedHeader>
                    <PinnedTitle>Pinned posts</PinnedTitle>
                    <span aria-hidden="true">📌</span>
                </PinnedHeader>
                {loading && (
                    <PinnedList aria-label="Loading pinned posts">
                        <PinnedSkeleton />
                        <PinnedSkeleton />
                    </PinnedList>
                )}
                {!loading && error && (
                    <PinnedState $error>
                        Pinned posts could not be loaded.
                        <div><FeedRetryButton type="button" onClick={onRetry}>Retry</FeedRetryButton></div>
                    </PinnedState>
                )}
                {!loading && !error && unpinError && (
                    <PinnedState $error role="alert">
                        This post could not be unpinned. Please try again.
                    </PinnedState>
                )}
                {!loading && !error && !pinnedNotices.length && (
                    <PinnedState>No pinned posts for this audience.</PinnedState>
                )}
                {!loading && !error && pinnedNotices.length > 0 && (
                    <PinnedList>
                        {pinnedNotices.map(notice => {
                            const creatorName = notice.postedBy || "Unavailable user";
                            const creatorIdentity = getNoticeAuthorIdentity(notice);
                            const publishedDate = notice.postedDate || notice.created_at;
                            const media = getNoticeMediaItems(notice)[0];
                            const thumbnailUrl = media?.mediaType === "video" ?
                                media.previewImageUrl :
                                media?.imageUrl;
                            const commentCount = getNoticeCommentCount(notice, audience);
                            return (
                                <PinnedPostItem key={notice.uuid}>
                                    <PinnedPostButton
                                        type="button"
                                        $hasPinButton={canManageActiveNoticeboard}
                                        onClick={() => {
                                            setShowReactions(false);
                                            setSelectedNotice(notice);
                                        }}
                                        aria-label={`Open pinned post ${notice.subject || "Noticeboard post"}`}
                                    >
                                        <PinnedThumbnail>
                                            <PinnedMediaThumbnail thumbnailUrl={thumbnailUrl} />
                                        </PinnedThumbnail>
                                        <PinnedContent>
                                            <PinnedPostTitle>{notice.subject || "Noticeboard post"}</PinnedPostTitle>
                                            <PinnedCreatorRow>
                                                <NoticeboardFeedAvatar
                                                    displayName={creatorIdentity.displayName}
                                                    profilePictureUrl={creatorIdentity.profilePictureUrl}
                                                    size="1.35rem"
                                                    fontSize="0.48rem"
                                                />
                                                <span>Created by {creatorName}</span>
                                            </PinnedCreatorRow>
                                            {notice.message && (
                                                <PinnedDescription>{notice.message}</PinnedDescription>
                                            )}
                                            <PinnedMeta>
                                                <time dateTime={publishedDate}>
                                                    {moment.utc(publishedDate).local().fromNow()}
                                                </time>
                                                <span>📌 Pinned</span>
                                                <span>{notice.reactions.total} reactions</span>
                                                <span>{commentCount} comments</span>
                                            </PinnedMeta>
                                        </PinnedContent>
                                    </PinnedPostButton>
                                    {canManageActiveNoticeboard && (
                                        <FeedPinIconButton
                                            type="button"
                                            $active
                                            $top="auto"
                                            $right="auto"
                                            $bottom="0.45rem"
                                            $left="0.65rem"
                                            disabled={Boolean(unpinLoadingUuid)}
                                            aria-busy={unpinLoadingUuid === notice.uuid}
                                            aria-label={`Unpin ${notice.subject || "Noticeboard post"}`}
                                            aria-pressed={true}
                                            title={unpinLoadingUuid === notice.uuid ?
                                                "Saving pinned status…" :
                                                "Unpin post"
                                            }
                                            onClick={() => handleUnpin(notice)}
                                        >
                                            <FeedPinIcon
                                                src={NoticePinIcon}
                                                $active
                                                alt=""
                                                aria-hidden="true"
                                            />
                                        </FeedPinIconButton>
                                    )}
                                </PinnedPostItem>
                            );
                        })}
                    </PinnedList>
                )}
            </PinnedSidebar>
            {selectedNotice && (
                <>
                    <NoticeboardPostDetailModal
                        open={Boolean(selectedNotice)}
                        onClose={handleCloseDetail}
                        token={token}
                        notice={selectedNotice}
                        interactionUuid={selectedInteractionUuid}
                        employeeName={employeeName}
                        clientName={clientName}
                        audience={audience}
                        showAudience={Boolean(audience)}
                        canUseCheckedIn={canUseCheckedIn}
                        onOpenReactions={() => setShowReactions(true)}
                        onCommentCreated={handleCommentCreated}
                    />
                    <NoticeboardReactionsModal
                        open={showReactions}
                        onClose={() => setShowReactions(false)}
                        token={token}
                        interactionUuid={selectedInteractionUuid}
                    />
                </>
            )}
        </>
    );
};

export default NoticeboardPinnedPosts;
